// ProductList.tsx
import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import styled from 'styled-components';
import { ProductOrBrand } from '../model/alternativeTypes';
import { FiPlus } from 'react-icons/fi';
import { Button } from '../components/Button';
import ProductEditor from './ProductEditor';
import ProductTile from './ProductTile';

interface ProductListProps {
  isAvoid: boolean;
  flyerLocale: string;
  categoryName: string;
  products: ProductOrBrand[];
  onReorder: (products: ProductOrBrand[]) => void;
  onChange: (product: ProductOrBrand) => void;
}

const ProductList: React.FC<ProductListProps> = ({ isAvoid, categoryName, flyerLocale, products, onReorder, onChange }) => {
  const [editingProduct, setEditingProduct] = useState<ProductOrBrand | null>(null);

  const handleOnDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(products);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    onReorder(items);
  };

  const handleSaveProduct = (save: ProductOrBrand) => {
    onChange(save)
    setEditingProduct(null);
  } 

  return (<>
    {!!editingProduct ? (
      <ProductEditor
        isAvoid={isAvoid}
        flyerLocale={flyerLocale} 
        categoryName={categoryName} 
        product={editingProduct} 
        onSave={(save) => handleSaveProduct(save)} 
        onCancel={() => setEditingProduct(null)}
      />
    ) : (
      <DragDropContext onDragEnd={handleOnDragEnd}>
        <Droppable droppableId="products" direction="vertical">
          {(provided) => (
            <ProductListContainer
              {...provided.droppableProps}
              ref={provided.innerRef}
            >
              {products.map((product, index) => (
                <Draggable key={product.name} draggableId={product.name} index={index}>
                  {(provided) => (
                    <ProductItem
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                    >
                      <DragHandle {...provided.dragHandleProps}>
                        &#x283F;
                      </DragHandle>
                      <ProductTile 
                        categoryName={categoryName}
                        flyerLocale={flyerLocale}
                        product={product} 
                        onChange={(p) => onChange(p)} 
                      />
                    </ProductItem>
                  )}
                </Draggable>
              ))}
              {editingProduct === null &&
              <Button 
                onClick={() => setEditingProduct({
                  name: '',
                  ownerCountry: 'OT',
                  categories: []
                })}
                style={{ height: '30px' }}
              >
                <FiPlus size={18} style={{ verticalAlign: 'middle' }} />
              </Button>}
              {provided.placeholder}
            </ProductListContainer>
          )}
        </Droppable>
      </DragDropContext>
    )}</>
  );
};

const ProductListContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  align-items: start;
  width: 100%;
  gap: 10px;
  padding: 10px;
  background-color: var(--side-bg-color);
  border-radius: 4px;
`;

const ProductItem = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  padding: 2px 10px;
  background-color: var(--control-bg-color);
  border: 1px solid var(--border-color);
  border-radius: 0.25rem;
`;

const DragHandle = styled.div`
  cursor: grab;
  margin-right: 10px;

  &:active {
    cursor: grabbing;
  }
`;

export default ProductList;
